import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    RightOutlined,
    ShoppingCartOutlined,
} from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getSelectedTenant } from 'services/tenant/tenantMembershipService';

const formatStatus = (value, fallback) => value
    ? String(value).toLowerCase().split(/[_-]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
    : fallback;

const getDateState = (value) => {
    if (!value) return { daysRemaining: null, expired: false };
    const end = new Date(value);
    if (Number.isNaN(end.getTime())) return { daysRemaining: null, expired: false };
    end.setHours(23, 59, 59, 999);
    const difference = end.getTime() - Date.now();
    return { daysRemaining: Math.max(0, Math.ceil(difference / 86400000)), expired: difference < 0 };
};

const formatDate = (value, fallback = 'Not available') => {
    if (!value) return fallback;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getSafeInvoiceUrl = (value) => {
    if (!value) return null;
    try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

export const resolveWorkspaceCommercialState = (workspace = {}) => {
    const subscription = workspace.subscriptionStatus;
    const payment = workspace.paymentStatus;
    const access = workspace.accessStatus;
    const endValue = workspace.temporaryAccessEndAt || workspace.temporaryAccessEnd;
    const dateState = getDateState(endValue);
    const overdue = ['PAYMENT_OVERDUE', 'GRACE_EXPIRED'].includes(subscription)
        || payment === 'OVERDUE'
        || access === 'READ_ONLY'
        || (access === 'TEMPORARY_ACCESS' && dateState.expired);

    if (overdue) return {
        kind: 'urgent', icon: <ExclamationCircleOutlined />, tag: 'Read-only access', tagColor: 'red',
        title: 'Payment is overdue',
        message: 'Your data remains safe. Complete payment to restore create and edit access.',
        action: 'Complete payment', destination: '/app/subscription?stage=invoice', showInvoice: true,
    };

    if (['PENDING_PAYMENT', 'PENDING_VERIFICATION'].includes(payment) && access === 'TEMPORARY_ACCESS') {
        const remaining = dateState.daysRemaining;
        const accessCopy = remaining === null
            ? `Temporary access is available until ${formatDate(endValue)}.`
            : remaining === 0 ? 'Temporary access ends today.' : `${remaining} day${remaining === 1 ? '' : 's'} of temporary access remaining.`;
        return {
            kind: remaining !== null && remaining <= 3 ? 'urgent' : 'pending',
            icon: <ClockCircleOutlined />,
            tag: payment === 'PENDING_VERIFICATION' ? 'Pending verification' : 'Invoice pending',
            tagColor: 'gold',
            title: payment === 'PENDING_VERIFICATION' ? 'Payment is being verified' : 'Your invoice is pending',
            message: `${accessCopy} Payment due: ${formatDate(workspace.paymentDueAt || workspace.paymentDue)}.`,
            action: 'View payment status', destination: '/app/subscription?stage=invoice', showInvoice: true,
        };
    }

    if (subscription === 'ACTIVE' || payment === 'PAID' || access === 'FULL_ACCESS') return {
        kind: 'success', icon: <CheckCircleOutlined />, tag: 'Full access', tagColor: 'green',
        title: `${workspace.plan || 'Your plan'} is active`,
        message: 'Payment is confirmed and all features included in this organization plan are available.',
        action: 'Manage subscription', destination: '/app/subscription?stage=manage', showInvoice: true,
    };

    if (subscription === 'PLAN_DRAFT' || (workspace.plan && workspace.plan !== 'Plan not selected')) return {
        kind: 'info', icon: <ShoppingCartOutlined />, tag: 'Plan draft', tagColor: 'blue',
        title: `${workspace.plan} has been selected`,
        message: 'You can continue setting up the organization now and confirm the order when you are ready.',
        action: 'Review plan', destination: '/app/subscription?stage=review', showInvoice: false,
    };

    return {
        kind: 'info', icon: <ShoppingCartOutlined />, tag: 'Exploration', tagColor: 'blue',
        title: 'Choose a plan when you are ready',
        message: 'Explore the Dashboard and create the organization profile before starting a paid plan.',
        action: 'Choose a plan', destination: '/onboarding?mode=change-plan', showInvoice: false,
    };
};

export default function PaymentStatusBanner() {
    const navigate = useNavigate();
    const workspace = getSelectedTenant();
    const state = resolveWorkspaceCommercialState(workspace || {});
    const invoiceUrl = getSafeInvoiceUrl(workspace?.invoiceUrl);
    const invoiceAvailable = Boolean(invoiceUrl);

    const openInvoice = () => {
        if (invoiceUrl) window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <section className={`dashboard-payment-banner dashboard-payment-banner--${state.kind}`}>
            <div className="dashboard-payment-banner__icon" aria-hidden="true">{state.icon}</div>
            <div className="dashboard-payment-banner__content">
                <div className="dashboard-payment-banner__title-row"><strong>{state.title}</strong><Tag color={state.tagColor}>{state.tag}</Tag></div>
                <p>{state.message}</p>
                <div className="dashboard-payment-banner__meta">
                    <span>Plan <strong>{workspace?.plan || 'Not selected'}</strong></span>
                    <span>Billing <strong>{formatStatus(workspace?.paymentStatus, 'Not started')}</strong></span>
                    <span>Access <strong>{formatStatus(workspace?.accessStatus, 'Exploration')}</strong></span>
                </div>
            </div>
            <div className="dashboard-payment-banner__actions">
                {state.showInvoice ? (
                    <Tooltip title={invoiceAvailable ? 'Open invoice' : 'Invoice will be available after it is issued.'}>
                        <span><Button disabled={!invoiceAvailable} icon={<FileTextOutlined />} onClick={openInvoice}>View invoice</Button></span>
                    </Tooltip>
                ) : null}
                <Button danger={state.kind === 'urgent'} type="primary" onClick={() => navigate(state.destination)}>
                    {state.action} <RightOutlined />
                </Button>
            </div>
        </section>
    );
}
